import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {Redirect, useHistory} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {pdf} from '@react-pdf/renderer';
import QuoteDesignerTakeover from './QuoteDesignerTakeover';
import SettingsPanel from './SettingsPanel';
import PreviewPane from './PreviewPane';
import QuotePDF from './pdf/QuotePDF';
import {buildDefaultQuoteTemplate, DEFAULT_FILE_NAME_TEMPLATE, EMPTY_QUOTE_TEMPLATE} from './config/defaultTemplate';
import {readTemplate} from './persistence/readTemplate';
import {withSection} from './persistence/quoteTemplateSchema';
import {serializeTemplate} from './persistence/serializeTemplate';
import {applyAccountDefaults} from './persistence/applyAccountDefaults';
import {MERGE_FIELDS} from './config/mergeFields';
import {resolveTemplate} from './resolver/resolveTemplate';
import {resolveDownloadFileName} from './resolver/resolveTemplateString';
import type {MergeContext, QuoteSectionType, QuoteTemplate} from './resolver/types';
import {addDaysToIsoDate, buildQuoteDocumentData, isExpiryWithinRange} from './quoteDocument';
import {prepareQuotePublish, PublishBlockedReason} from './publishQuote';
import {showPublishQuoteConfirmationModal} from './PublishQuoteConfirmationModal';
import {showDiscardQuoteModal} from './modals/DiscardQuoteModal';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {Alert} from '@forecast-it/design-system';
import type {QuoteContentBlock} from './persistence/sourceBlockState';
import CreateQuoteMutation from '../../../mutations/create_quote_mutation';
import CreateQuoteContentBlockMutation from '../../../mutations/create_quote_content_block_mutation';
import UpdateQuoteContentBlockMutation from '../../../mutations/update_quote_content_block_mutation';
import SetQuoteBrandingDefaultMutation from '../../../mutations/set_quote_branding_default_mutation';
import Util from '../../../forecast-app/shared/util/util';
import ProjectUtil from '../../../forecast-app/shared/util/ProjectUtil';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {isQuotesEnabled} from '../../../forecast-app/shared/util/FeatureUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {projectUrl} from '../../../directApi';
import {getProjectIndicatorString} from '../../../forecast-app/shared/components/project-indicator/support/ProjectIndicatorLogic';
import {useSecondaryNavState} from '../../../forecast-app/navigation/secondary/SecondaryNavigationStateManager';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {isLiveQuote} from '../quote-activity/quoteActivityCopy';

const Layout = styled.div`
	flex: 1 1 auto;
	min-height: 0;
	display: grid;
	grid-template-columns: 472px 1fr;
`;
const SettingsCol = styled.div`
	/* Grid items default to min-height auto, which lets content stretch the track — the
	   explicit floor is what makes the column scroll instead of growing past the pane. */
	min-height: 0;
	overflow-y: auto;
	border-right: 1px solid ${({theme}) => theme.sys.color.border.decorative};
`;
const PreviewCol = styled.div`
	background: #f8f8f8;
	display: flex;
	align-items: stretch;
	min-height: 0;
	overflow: hidden;
`;
const ErrorBanner = styled.div`
	margin: 16px 24px 0 24px;
	padding: 12px 16px;
	border: 1px solid #c00;
	border-radius: 4px;
	color: #c00;
	font-size: 13px;
`;

/** The slice of the publish response this page reads back — see create_quote_mutation. */
type PublishResponse = {
	createQuote?: {project?: {quote?: {revisionNumber?: number | null} | null} | null} | null;
};

type Props = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Relay-supplied; shape enforced by the QuoteDesignerPage_viewer fragment
	viewer: any;
	projectId?: string;
};

export const QuoteDesignerPageInner: React.FC<Props> = ({viewer}) => {
	const intl = useIntl();
	const history = useHistory();
	useTrackPage('Quote Designer');

	const project = viewer.project;
	const company = viewer.company;
	const baselineUrl = project ? `${projectUrl(project.companyProjectId, project.customProjectId)}/baseline` : '/';

	const existingQuote = project?.quote ?? null;
	// A REVISING quote is NOT "already built": project.quote returns the latest row by revision
	// regardless of status, so STATUS — not existence — decides whether this route is a create or a
	// bounce. The same isLiveQuote the Baseline's two CTA gates use.
	const isViewMode = isLiveQuote(existingQuote?.status);
	const liveData = useMemo(
		() =>
			project && company && !isViewMode
				? buildQuoteDocumentData(project, company, {
						deliverablesOn: ProjectUtil.deliverablesUiEnabled(project),
						// The version THIS publish will file, not the one on screen before it. Passing
						// nothing leaves buildQuoteDocumentData on its `?? 1` default, which was right
						// only while revision 1 was the sole revision that could exist - every later
						// revision then reads v1 in the settings panel, the preview AND the generated
						// PDF's cover page, which is stored and customer-facing.
						//
						// project.quote is the latest row by revision whatever its status, so after a
						// revise it is the REVISING row and the next number is its + 1; with no quote at
						// all this is revision 1. It predicts what the server will assign rather than
						// reading it, because the PDF is built here and posted WITH the publish.
						revision: (existingQuote?.revisionNumber ?? 0) + 1,
				  })
				: null,
		[project, company, isViewMode, existingQuote]
	);

	// A revise starts from the version it revises; a new quote starts from the built-in copy with
	// the account's defaults overlaid. Account state seeds a NEW quote only — re-applying it on a
	// revise would let a block edited since the last publish rewrite what that version said.
	const [template, setTemplate] = useState<QuoteTemplate>(() => {
		if (!liveData) return EMPTY_QUOTE_TEMPLATE;
		const base = buildDefaultQuoteTemplate(intl, liveData);
		// A quote row that exists but is not live is a REVISING one — the same status split
		// isViewMode already makes, rather than a second spelling of the status set.
		if (existingQuote != null && !isViewMode) {
			return readTemplate(existingQuote.templateJson, base);
		}
		return applyAccountDefaults(base, company?.quoteBrandingDefault ?? null, company?.quoteContentBlocks ?? []);
	});
	// Per-quote state, deliberately NOT in the design payload: a stored expiry would reopen in the
	// past on a revise. quote.expiry_date remains the per-version record of what was promised.
	const [expiryDate, setExpiryDate] = useState<string>(() => liveData?.expiryDate ?? '');
	const minExpiryDate = useMemo(() => (liveData ? addDaysToIsoDate(liveData.issuedDate, 1) : ''), [liveData]);
	// The picker reports every complete date its segments spell, including ones before the minimum
	// and ones whose year is still half-typed - deleting a digit from 2026 spells the year 202, and
	// that is a date the picker is happy to emit. It marks itself invalid, but the value would
	// otherwise flow straight to the cover, the PDF and the publish, which is how a quote came to
	// be stored as expiring in the year 202.
	//
	// A rejected value is held back rather than corrected: the field keeps showing what was typed,
	// so the next keystroke can finish the year, while the document keeps the last date that was
	// actually valid and publishing is blocked until it is.
	const [expiryRejected, setExpiryRejected] = useState(false);
	const handleExpiryChange = useCallback(
		(iso: string) => {
			if (!isExpiryWithinRange(iso, minExpiryDate)) {
				setExpiryRejected(true);
				return;
			}
			setExpiryRejected(false);
			setExpiryDate(iso);
		},
		[minExpiryDate]
	);
	// What every renderer reads: the baseline facts with the user's expiry in place of the default.
	const documentData = useMemo(
		() => (liveData ? {...liveData, expiryDate: expiryDate || liveData.expiryDate} : null),
		[liveData, expiryDate]
	);

	const [publishing, setPublishing] = useState(false);
	const [publishBlocked, setPublishBlocked] = useState<PublishBlockedReason | null>(null);

	const mergeContext: MergeContext | null = useMemo(
		() => (documentData ? {data: documentData, intl} : null),
		[documentData, intl]
	);

	// The designer wants the width, and an expanded project nav invites navigating away mid-edit,
	// so it collapses to its rail while this page is mounted and is restored on leave — restored
	// only when it was expanded on entry, or leaving would expand a nav the user had closed
	// themselves. The ref keeps the effect mount-only: the provider rebuilds its callbacks every
	// render and collapsing flips `expanded`, so depending on either would re-run the effect
	// against its own change.
	const navState = useSecondaryNavState();
	const navStateRef = useRef(navState);
	navStateRef.current = navState;
	useEffect(() => {
		const nav = navStateRef.current;
		if (!nav?.expanded || !nav.forceCollapse) return undefined;
		nav.forceCollapse();
		return () => navStateRef.current?.forceExpand?.();
	}, []);

	const contentBlocks: readonly QuoteContentBlock[] = company?.quoteContentBlocks ?? [];

	const confirm = useCallback(
		(messageId: string, values?: Record<string, string>) =>
			createToast({
				duration: 5000,
				variant: 'banner',
				message: <Alert type={Alert.TYPE.SUCCESS} text={intl.formatMessage({id: messageId}, values)} />,
			}),
		[intl]
	);

	// Overwrite an existing block with the section's current copy. The section keeps pointing at
	// the same block, so the bar reads clean again once the store updates.
	const handleSaveBlock = useCallback(
		(block: QuoteContentBlock, bodyJson: string) => {
			Util.CommitMutation(UpdateQuoteContentBlockMutation, {id: block.id, name: block.name, bodyJson}, () =>
				confirm('quote_designer.content_block.saved_toast', {name: block.name})
			);
		},
		[confirm]
	);

	// Record where the section's copy now comes from. Without this the section stays
	// provenance-less: the bar goes on inviting the author to choose a version, the name they just
	// saved never reads as the one in use, and Save routes back to Save-as - so the next press
	// creates a second block under the same name rather than overwriting the first.
	const handleSaveBlockAs = useCallback(
		(sectionType: QuoteSectionType, name: string, bodyJson: string) => {
			Util.CommitMutation(CreateQuoteContentBlockMutation, {sectionType, name, bodyJson}, (response: unknown) => {
				const saved = (response as {createQuoteContentBlock?: {quoteContentBlock?: {id?: string}}})
					?.createQuoteContentBlock?.quoteContentBlock;
				if (saved?.id) {
					setTemplate(current => withSection(current, sectionType, {sourceBlockId: saved.id as string}));
				}
				confirm('quote_designer.content_block.saved_toast', {name});
			});
		},
		[confirm]
	);

	// Sending only isDefault would blank the block: the mutation's name and body are non-null, so
	// the current values are resent alongside it.
	const handleSetBlockDefault = useCallback(
		(block: QuoteContentBlock) => {
			Util.CommitMutation(
				UpdateQuoteContentBlockMutation,
				{id: block.id, name: block.name, bodyJson: block.bodyJson, isDefault: true},
				() => confirm('quote_designer.content_block.default_toast', {name: block.name})
			);
		},
		[confirm]
	);

	const handleSaveBrandingDefault = useCallback(
		(branding: {accentColor: string; font: string}) => {
			Util.CommitMutation(SetQuoteBrandingDefaultMutation, branding, () =>
				confirm('quote_designer.branding.default_saved')
			);
		},
		[confirm]
	);

	// Nothing is persisted until the quote is created, so cancelling loses the whole
	// configuration — it asks first.
	const handleCancel = useCallback(() => {
		showDiscardQuoteModal({onDiscard: () => history.push(baselineUrl)});
	}, [history, baselineUrl]);

	const handleDownload = useCallback(async () => {
		if (!documentData) return;
		const resolved = resolveTemplate(template, {data: documentData, intl});
		const blob = await pdf(<QuotePDF data={documentData} template={resolved} intl={intl} />).toBlob();
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = resolveDownloadFileName(DEFAULT_FILE_NAME_TEMPLATE, {data: documentData, intl});
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		// Deferred: Safari (and some Firefox versions) abort the download when the blob URL is
		// revoked in the same tick as the click.
		setTimeout(() => URL.revokeObjectURL(url), 0);
	}, [documentData, template, intl]);

	const doPublish = useCallback(async () => {
		if (!documentData || !project) return;
		const resolved = resolveTemplate(template, {data: documentData, intl});
		const blob = await pdf(<QuotePDF data={documentData} template={resolved} intl={intl} />).toBlob();
		// The uploaded file keeps the served naming convention (the company service names the filed
		// row itself); the Export file name only names local downloads.
		const prepared = prepareQuotePublish(blob, `Quote-${documentData.projectIndicator}-v1.pdf`);
		if (!prepared.ok) {
			setPublishBlocked(prepared.reason);
			return;
		}
		setPublishBlocked(null);
		setPublishing(true);
		Util.CommitMutation(
			CreateQuoteMutation,
			{
				projectId: project.id,
				expiryDate: documentData.expiryDate,
				// The AUTHORED template, not `resolved`: the stored payload keeps its merge tokens so a
				// revise re-resolves them against the live project rather than against whatever they
				// meant on the day this version was published.
				templateJson: serializeTemplate(template),
			},
			(response: PublishResponse) => {
				// The revision comes from the row the server just wrote, not a literal: once a
				// revise flow can publish a second quote, a hard-coded 1 would report every
				// revision as the first and silently flatten the revision-volume metric. Left
				// absent rather than defaulted if the response shape ever changes — an absent
				// property is honest, a wrong one is not.
				trackEvent('Quote', 'Published', {
					projectId: project.companyProjectId,
					companyId: company?.id,
					revisionNumber: response?.createQuote?.project?.quote?.revisionNumber,
				});
				history.push(baselineUrl);
			},
			true,
			{file: prepared.file},
			(transaction: unknown, onFailure: (t: unknown) => unknown) => {
				// onFailure answers true when it has scheduled its own retry of this mutation —
				// the button must stay disabled through that window or a second click publishes twice.
				const retryScheduled = onFailure(transaction);
				if (retryScheduled !== true) {
					setPublishing(false);
				}
			}
		);
	}, [documentData, project, company, template, intl, history, baselineUrl]);

	const handlePublishClick = useCallback(() => {
		showPublishQuoteConfirmationModal({onConfirm: doPublish});
	}, [doPublish]);

	// Direct-URL guards: the header CTAs apply the same gates, but the URL is deep-linkable.
	// The designer is create-only — a LIVE quote is viewed as its PDF in a browser tab (the
	// Baseline header's View quote opens it), so with a live quote in place this URL just returns
	// to the Baseline. A revising quote falls through and designs revision n+1.
	if (!isQuotesEnabled() || !project || isViewMode) {
		return <Redirect to={baselineUrl} />;
	}
	if (!hasPermission(PERMISSION_TYPE.QUOTE_CREATE) || !liveData || liveData.lineItems.length === 0) {
		return <Redirect to={baselineUrl} />;
	}

	return (
		<QuoteDesignerTakeover
			closeLabel={intl.formatMessage({id: 'quote_designer.takeover.close'})}
			title={intl.formatMessage({id: 'quote_designer.takeover.title'})}
			projectName={project.name || ''}
			projectIndicator={getProjectIndicatorString(project.companyProjectId, project.customProjectId)}
			projectColor={project.projectColor}
			cancelLabel={intl.formatMessage({id: 'quote_designer.cancel'})}
			onCancel={handleCancel}
			downloadLabel={intl.formatMessage({id: 'quote_designer.download'})}
			onDownload={handleDownload}
			primaryLabel={intl.formatMessage({id: 'quote_designer.publish'})}
			onPrimary={handlePublishClick}
			primaryDisabled={publishing || expiryRejected}
		>
			{publishBlocked && (
				<ErrorBanner data-cy="quote-publish-blocked">
					{intl.formatMessage({id: 'quote_designer.error.pdf_too_large'})}
				</ErrorBanner>
			)}
			<Layout>
				{liveData && (
					<SettingsCol>
						<SettingsPanel
							template={template}
							onChange={setTemplate}
							mergeFields={MERGE_FIELDS}
							data={documentData ?? liveData}
							expiryDate={expiryDate || liveData.expiryDate}
							minExpiryDate={minExpiryDate}
							onExpiryChange={handleExpiryChange}
							expiryError={
								expiryRejected
									? intl.formatMessage({id: 'quote_designer.cover.valid_until_invalid'})
									: undefined
							}
							contentBlocks={contentBlocks}
							onSaveBlock={handleSaveBlock}
							onSaveBlockAs={handleSaveBlockAs}
							onSetBlockDefault={handleSetBlockDefault}
							brandingDefault={company?.quoteBrandingDefault ?? null}
							onSaveBrandingDefault={handleSaveBrandingDefault}
						/>
					</SettingsCol>
				)}
				<PreviewCol>{mergeContext && <PreviewPane template={template} mergeContext={mergeContext} />}</PreviewCol>
			</Layout>
		</QuoteDesignerTakeover>
	);
};

const QuoteDesignerPage = createFragmentContainer(QuoteDesignerPageInner, {
	viewer: graphql`
		fragment QuoteDesignerPage_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
			company {
				id
				name
				logoId
				currency
				# The running footer carries it beside the company name, as the design does.
				contactEmail
				quoteBrandingDefault {
					accentColor
					font
				}
				# All three section types in one round trip: the designer shows every section at once.
				quoteContentBlocks {
					id
					sectionType
					name
					bodyJson
					isDefault
				}
			}
			project(id: $projectId) {
				id
				name
				companyProjectId
				customProjectId
				projectColor
				useDeliverables
				budgetType
				baselineEstimationMinutesPerDay
				salesperson {
					id
					fullName
				}
				rateCard {
					id
					currency
				}
				client {
					id
					name
					logoId
				}
				quote {
					id
					status
					revisionNumber
					templateJson
				}
				phases(first: 1000000) {
					edges {
						node {
							id
							name
						}
					}
				}
				deliverables(first: 1000000) {
					edges {
						node {
							id
							name
						}
					}
				}
				phaseBaselineExpenses(first: 1000000) {
					edges {
						node {
							id
							expenseName
							expenseCategory {
								name
								id
							}
							expenseRevenue
							expenseCost
							expenseMarkup
							expenseProfit
							globalPhaseId
							deliverableId
						}
					}
				}
				phaseBaselineRoles(first: 1000000) {
					edges {
						node {
							id
							globalPhaseId
							deliverableId
							baselineMinutes
							role {
								name
								id
							}
							financialNumbers(convertToProjectCurrency: true) {
								baselineTimeAndExpenses
								baselineRevenue
								baselineCost
								baselineProfit
								baselineRatePerHour
								baselineCostPerHour
							}
						}
					}
				}
			}
		}
	`,
});

export default QuoteDesignerPage;
